import styles from './radio-button.module.scss';
import cn from 'classnames';
import { InputHTMLAttributes } from 'react';

export interface RadioOption<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

export interface RadioInputProps<T> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size'> {
  additionalClass?: string;
  /** @deprecated Use size="sm" instead */
  formCheckAdditionalClass?: string;
  isSmallSize?: boolean;
  isSolid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  direction?: 'horizontal' | 'vertical';
  variant?: 'red' | 'blue';
  options: RadioOption<T>[];
  value?: T;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: T) => void;
  valueToString?: (value: T) => string;
  stringToValue?: (str: string) => T;
}

export const RadioInput = <T,>({
  additionalClass = '',
  formCheckAdditionalClass = '',
  isSmallSize = false,
  isSolid = false,
  size = 'md',
  direction = 'horizontal',
  variant = 'red',
  options,
  name,
  value,
  onChange,
  valueToString,
  stringToValue,
  ...rest
}: RadioInputProps<T>) => {
  const firstOptionValue = options[0]?.value;
  const resolvedSize = isSmallSize ? 'sm' : size;

  const getValueToString =
    valueToString ||
    ((val: T): string => {
      if (typeof val === 'boolean') {
        return val ? 'true' : 'false';
      }
      if (typeof val === 'number') {
        return val.toString();
      }
      return String(val);
    });

  const getStringToValue =
    stringToValue ||
    ((str: string): T => {
      if (typeof firstOptionValue === 'boolean') {
        return (str === 'true') as T;
      }
      if (typeof firstOptionValue === 'number') {
        return Number(str) as T;
      }
      return str as T;
    });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = getStringToValue(event.target.value);

    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: selectedValue,
        name: event.target.name
      }
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent, selectedValue);
  };

  const isOptionChecked = (optionValue: T): boolean => {
    if (value === undefined) return false;
    return optionValue === value;
  };

  const inputSizeClassName = cn({
    'w-20px h-20px': resolvedSize === 'sm',
    'w-23px h-23px': resolvedSize === 'md',
    'w-26px h-26px': resolvedSize === 'lg'
  });

  return (
    <div
      className={cn('d-flex', additionalClass, {
        [styles.radioBlue]: variant === 'blue',
        [styles.radioRed]: variant === 'red'
      })}
    >
      <div
        className={cn('d-flex gap-3 w-100', {
          'flex-column': direction === 'vertical',
          'align-items-center': direction === 'horizontal'
        })}
      >
        {options.map((option, index) => {
          const optionValue = getValueToString(option.value);
          const optionId = `${name}-${optionValue}-${index}`;
          const isChecked = isOptionChecked(option.value);

          return (
            <div
              key={optionId}
              className={cn(
                'form-check form-check-custom',
                {
                  'form-check-solid': isSolid
                },
                formCheckAdditionalClass
              )}
            >
              <input
                type="radio"
                value={optionValue}
                id={optionId}
                name={name}
                checked={isChecked}
                onChange={handleChange}
                disabled={option.disabled || rest.disabled}
                className={cn('form-check-input', inputSizeClassName)}
                {...rest}
              />
              <label htmlFor={optionId} className="form-check-label text-gray-700">
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};
